import {
  ChangeDetectorRef,
  Directive,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import {Subscription} from 'rxjs';
import {NgIf} from '@angular/common';
import {BLOCKS_CONFIG} from '@blocks/blocks.config';
import {Block, RexCalculationType} from '@paperlessio/sdk/api/models';
import {startWith} from 'rxjs/operators';
import {CalculatedBlockAttributeStore} from '@shared/rex/calculated-block-attribute.store';
import {DataSetInputRowComponent} from '@blocks/input/data-set-input/data-set-input-row/data-set-input-row.component';

@Directive({
  selector: '[visibleBlock]',
  standalone: true
})
export class BlockVisibilityDirective implements OnInit, OnChanges, OnDestroy {
  @Input('visibleBlock') block: Block;

  private templateRef = inject(TemplateRef);
  private viewContainer = inject(ViewContainerRef);
  private dataSetInputRowComponent = inject(DataSetInputRowComponent, {skipSelf: true, optional: true});
  private blocksConfig = inject(BLOCKS_CONFIG);
  private calculatedBlockAttributeStore = inject(CalculatedBlockAttributeStore);
  private cdr = inject(ChangeDetectorRef);

  private subs = new Subscription();
  private ngIfDirective = new NgIf(this.viewContainer, this.templateRef);

  private visible: boolean;
  private initialVisibility: boolean;

  constructor() {
    // as early as possible, set the initial visibility value of the directive
    // true if blocks are not excludable, false if they are
    this.initialVisibility = !this.blocksConfig.excludable;
    this.visible = this.initialVisibility;
    this.ngIfDirective.ngIf = this.initialVisibility;
  }

  ngOnInit() {
    // if the block's visibility is not calculated, set the initial visibility value to true
    if (!this.block.visibleCalculation) {
      this.initialVisibility = true;
    }

    this.subs.add(
      this.calculatedBlockAttributeStore.blockAttributeValue<boolean>(this.virtualSlug, 'visible').pipe(
        startWith(this.initialVisibility),
      ).subscribe(visible => {
        this.update(visible);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    switch (this.block.visible_calculation_type) {
      case RexCalculationType.CONSTANT:
        this.update(this.block.visible);
        break;
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private update(_visible: boolean) {
    const visible = !this.blocksConfig.excludable || _visible;
    const previouslyVisible = this.visible;
    if (previouslyVisible !== visible) {
      this.visible = visible;
      this.ngIfDirective.ngIf = visible;
      this.cdr.markForCheck();
    }
  }

  private get virtualSlug() {
    // TODO: memoize / share with block component
    const parentSlug = this.dataSetInputRowComponent?.virtualSlug ?? [];
    return parentSlug.length ? [...parentSlug, this.block.slug].join('.') : this.block.slug;
  }
}
